import './JourneyMapEditor.css';
import React from "react";
import {ZenObservable} from "zen-observable-ts/lib/types";
import {useNavigate, useParams} from "react-router";
import {useTranslation} from "react-i18next";
import html2canvas from "html2canvas";
import "../i18n/config";
import {JourneyMapOperation, MapOperation} from "../API";
import * as CJMapOp from "../models/Operation_gen";
import {fetchJourneyMapOperations, removeOperation} from "../graphql/MapOperation";
import {
    getMapMeta,
    updateMapPreview,
    updateMapTitle,
    updateMapVersion
} from "../graphql/JourneyMap";
import {debug_log} from "../utils/debug";
import {array_separate} from "../utils/functions";
import {CJMapManager} from "../models/CJmapManager";
import {ContentKey, ContentKeyPair, FunnelKey, RoleKey, RoleKeyPair} from "../models/Keys";
import {ManagerHistory} from "../models/ManagerHistory";
import {CellContent, EditorType} from "../models/CellContent";
import {Operation} from "../models/Operation";
import {Rect} from "../models/Rect";
import {Tooltip} from "./common/Tooltip";
import {ClickableInlineEditor} from "./JourneyMapEditor/InlineEditor";
import {ColorSelectorButton} from "./JourneyMapEditor/ColorSelector";
import {DropMenu} from "./JourneyMapEditor/DropMenu";
import {BackgroundImageSelector} from "./JourneyMapEditor/BackgroundImageSelector";
import {ColorSchemeSelector} from "./JourneyMapEditor/ColorSchemeSelector";
import {MessageBar} from "./JourneyMapEditor/MessageBar";
import {RCContentCell} from "./JourneyMapEditor/RCContentCell";
import {RCRoleHeader} from "./JourneyMapEditor/RCRoleHeader";
import {RCMainFunnelLine} from "./JourneyMapEditor/RCMainFunnelLine";
import {RCContentHeader} from "./JourneyMapEditor/RCContentHeader";
import {RCFunnelGroups} from "./JourneyMapEditor/RCFunnelGroups";
import {RCRoleCells} from "./JourneyMapEditor/RCRoleCells";
import {useSetStyleRegistory} from "../hooks/useSetStyleRegistory";
import {VisibleRoleCell} from "../models/RoleCell";
import {VisibleFunnelGroupCell} from "../models/FunnelGroupLine";
import {VisibleCell} from "../models/VisibleCell";
import {BoldButton} from "./JourneyMapEditor/BoldButton";
import {AlignmentButtons} from "./JourneyMapEditor/AlignmentButton";
import {AlignmentType} from "./JourneyMapEditor/CellEditor/EditorCommon";
import {KeyboardEventUtil} from "../utils/KeyboardEventUtil";
import {MovingCover} from "./common/MovingCover";
import {INITIAL_PERSONA_WIDTH, Persona} from "./JourneyMapEditor/Persona";
import {PersonaButton} from "./JourneyMapEditor/PersonaButton";
import {
    fetchJourneyMapOperation,
    fetchJourneyMapOperationsEx, fetchJourneyMapOperationsEx2,
    removeOperationEx,
    storeJourneyMapOperationsEx
} from "../graphql/JourneyMapOperation";
import {SerializableJourneyMap} from "../models/SerializableJourneyMap";
import {capturePreview} from "../utils/capture";
import {SequenceManager} from "../models/SequenceManager";
import {MAP_VERSION1, MAP_VERSION2, MapMeta} from "../models/MapMeta";
import {CollaboratorDialog} from "./Collaboration/CollaboratorDialog";
import {pushAnnouncement, subscribeAnnouncement} from "../graphql/Announcement";
import {MapUpdate} from "../models/Announcement/MapUpdate";
import {UserFocus} from "../models/Announcement/UserFocus";
import {useUserInfo} from "../hooks/useUserInfo";
import {MapAnnouncementTypeGuard} from "../models/Announcement/MapAnnouncementType";
import {UserOutFocus} from "../models/Announcement/UserOutFocus";
import {getUserEmailOrNullFromUserId} from "../graphql/Collaborator";
import {UserEnter} from "../models/Announcement/UserEnter";
import {UserLeave} from "../models/Announcement/UserLeave";
import {NonOwnerCollaboratorDialog} from "./Collaboration/NonOwnerCollaboratorDialog";
import {ConfirmDialog} from "./common/ConfirmDialog";
import {API, graphqlOperation} from "aws-amplify";
import {revokeMapAccess} from "../graphql/mutations";
import {OnlineUserDialog} from "./Collaboration/OnlineUserDialog";
import {ShortcutPanel} from "./JourneyMapEditor/ShortcutPanel";

const mgr_history = new ManagerHistory();
const sequence_manager = new SequenceManager();


async function loadV2Operations(mapId: string): Promise<CJMapManager> {

    function run(raw_operations: JourneyMapOperation[]): JourneyMapOperation | null {
        mgr_history.clear();
        sequence_manager.initialize(mgr_history);


        for (const raw_op of raw_operations) {
            try {
                sequence_manager.run(raw_op);
            } catch (e) {
                debug_log("Exception", e, raw_op);
                const start_repair = window.confirm("このファイルは壊れています。\n"
                    + "修復を試みることで回復することがありますが、一部作業が失われる可能性があります。\n\n"
                    + "OK:修復を開始します\n"
                    + "キャンセル: 修復せずに戻ります");

                if (start_repair) {
                    return raw_op;
                }

                throw e;
            }
        }

        return null;
    }

    const raw_map_operations = await fetchJourneyMapOperationsEx(mapId);
    debug_log("fetch operations", raw_map_operations);

    let try_operations = raw_map_operations;
    let error_op: JourneyMapOperation | null = null;
    while (null !== (error_op = run(try_operations))) {
        const target_seq = error_op.seq;
        // @ts-ignore
        const [retry_operations, error_operations] = array_separate(try_operations, raw_op => raw_op.seq === target_seq);
        await removeOperationEx(error_operations);
        try_operations = retry_operations;
    }

    debug_log("end operations", mgr_history.LastManager);

    return mgr_history.LastManager;
}

async function loadV1Operations(mapId: string): Promise<CJMapManager> {

    const raw_map_operations = await fetchJourneyMapOperations(mapId);
    debug_log("fetch operations", raw_map_operations);

    function run(mgr: CJMapManager, raw_operations: MapOperation[]): MapOperation | null {
        let last_mgr: CJMapManager = mgr;

        mgr_history.clear();


        for (const raw_op of raw_operations) {
            try {
                const op = Operation.fromStoredData(raw_op);
                debug_log("run", op);
                [, last_mgr] = mgr_history.run(last_mgr, op);
            } catch (e) {
                debug_log("Exception", e, raw_op);
                const start_repair = window.confirm("このファイルは壊れています。\n"
                    + "修復を試みることで回復することがありますが、一部作業が失われる可能性があります。\n\n"
                    + "OK:修復を開始します\n"
                    + "キャンセル: 修復せずに戻ります");

                if (start_repair) {
                    return raw_op;
                }

                throw e;
            }
        }

        return null;
    }

    const first_mgr = mgr_history.LastManager;
    let try_operations = raw_map_operations;
    let error_op: MapOperation | null = null;
    while (null !== (error_op = run(first_mgr, try_operations))) {
        const target_id = error_op.id;
        // @ts-ignore
        const [retry_operations, error_operations] = array_separate(try_operations, raw_op => raw_op.id === target_id);
        await removeOperation(error_operations);
        try_operations = retry_operations;
    }

    debug_log("end operations", mgr_history.LastManager);

    return mgr_history.LastManager;
}

async function loadJourneyMap(map_meta: MapMeta): Promise<CJMapManager> {

    if (map_meta.Version === MAP_VERSION1) {
        const manager = await loadV1Operations(map_meta.MapId);
        await storeJourneyMapOperationsEx(
            map_meta.MapId,
            new CJMapOp.OpUpdateJourneyMap({journey_map: SerializableJourneyMap.fromJSONString(manager.toJourneyMapJSONString())})
        );
        await updateMapVersion(map_meta.MapId, MAP_VERSION2);
        return manager;
    } else {
        return await loadV2Operations(map_meta.MapId);
    }

}

export const JourneyMapEditor = () => {

    const {mapId} = useParams<{ mapId: string }>();
    if (mapId === undefined) {
        throw new Error("no mapId");
    }
    const {userName} = useUserInfo();

    const navigate = useNavigate();
    const {t} = useTranslation();
    const setStyleRegistry = useSetStyleRegistory();
    const [map_meta, setMapMeta] = React.useState<MapMeta>(MapMeta.createReadOnly(mapId));
    const [zoom, setZoom] = React.useState(1.00);
    const [is_loading, setIsLoading] = React.useState(true);
    const [show_online_user_dialog, setShowOnlineUserDialog] = React.useState(false);
    const [show_collaborator_dialog, setShowCollaboratorDialog] = React.useState(false);
    const [show_confirm_revoke, setShowConfirmRevoke] = React.useState<boolean>(false);
    const [show_shortcut_panel, setShowShortcutPanel] = React.useState(false);
    const [persona_width, setPersonaWidth] = React.useState(INITIAL_PERSONA_WIDTH);
    const [mgr, setMgr] = React.useState<CJMapManager>(CJMapManager.createNew());
    mgr.setRefresher((mgr) => setMgr(mgr));

    React.useEffect(()=> {
        sequence_manager.setCurrentMgr(mgr);
    }, [mgr]);


    mgr.onUpdateSelection = (before, after) => {
        if (!userName) {
            return;
        }


        if (after.NotSelected) {
            pushAnnouncement(new UserOutFocus(mapId, userName));
        } else {
            pushAnnouncement(new UserFocus(mapId, userName, after));
        }
    };

    // 別画面操作の取得
    React.useEffect(() => {
        if (is_loading || !userName) {
            return;
        }

        let subscribe: ZenObservable.Subscription | undefined;
        subscribeAnnouncement(async (announcement) => {
            if (announcement.MapId !== mapId ) {
                return;
            }

            if (MapAnnouncementTypeGuard.isUpdate(announcement)) {
                debug_log("[announcement] receive update", sequence_manager.LastSeq);

                await sequence_manager.Mutex.runAsyncAfterUnlocked(async () => {
                    const ops = await fetchJourneyMapOperationsEx2(mapId, sequence_manager.LastSeq + 1);
                    if (0 < ops.length) {
                        debug_log("[scheduleOps] ", ops);
                        sequence_manager.scheduleOps(ops);
                    }
                });
            }

            if (MapAnnouncementTypeGuard.isFocus(announcement)) {
                if (announcement.UserId !== userName) {
                    setMgr(mgr => {
                        const new_mgr = mgr.updateCollaboratorFocus(announcement.UserId, announcement.Selection);

                        if (!mgr.hasCollaboratorFocusUserEmail(announcement.UserId)) {
                            const user_id = announcement.UserId;
                            getUserEmailOrNullFromUserId(user_id).then((email_or_null) => {
                                if (email_or_null !== null) {
                                    setMgr(mgr => mgr.updateCollaboratorFocusUser(user_id, email_or_null));
                                }
                            });
                        }

                        return new_mgr;
                    });
                }
            } else if (MapAnnouncementTypeGuard.isOutFocus(announcement)) {
                if (announcement.UserId !== userName) {
                    setMgr(mgr => mgr.removeCollaboratorFocus(announcement.UserId));
                }
            } else if (MapAnnouncementTypeGuard.isEnter(announcement)) {
                if (announcement.UserId !== userName) {
                    setMgr(mgr => {
                        if (!mgr.hasCollaboratorFocusUserEmail(announcement.UserId)) {
                            const user_id = announcement.UserId;
                            getUserEmailOrNullFromUserId(user_id).then((email_or_null) => {
                                if (email_or_null !== null) {
                                    setMgr(mgr => mgr.updateCollaboratorFocusUser(user_id, email_or_null));
                                }
                            });

                            // 未登録のユーザ = 新たにオンラインになったユーザ なので、
                            // そのユーザに現在のオンラインユーザを伝えるためのアナウンス
                            // 即時で返すと反映がうまくいかなかったので、1秒ほどウェイト
                            setTimeout(() => pushAnnouncement(new UserEnter(mapId, userName)), 1000);
                        }
                        return mgr;
                    });
                }
            } else if (MapAnnouncementTypeGuard.isLeave(announcement)) {
                if (announcement.UserId !== userName) {
                    setMgr(mgr => mgr.removeCollaboratorFocusUser(announcement.UserId));
                }
            }
        }).then(s => subscribe = s);

        // 良い場所が見当たらなかったので、ひとまずここで
        // マップ読み込み後に一度だけ呼ばれる想定
        pushAnnouncement(new UserEnter(mapId, userName));

        return () => {
            subscribe && subscribe.unsubscribe();
        }
    }, [userName, mapId, is_loading] /* 不必要な subscribe を避けるため、mgr を指定しないこと */);

    // 別画面操作の反映
    React.useEffect(() => {
        if (is_loading) {
            return;
        }

        if (mgr.Selection.ContentCellIsEdit) {
            return;
        }

        const interval = setInterval(() => {
            try {
                const mgr = sequence_manager.applyOps();
                if (mgr !== null) {
                    setMgr(mgr);
                }
            } catch (err) {
                console.error(err instanceof Error ? err.message : err)
                window.alert("エラーが発生しました。マップを再読み込みします");
                window.location.reload()
            }
        }, 500);
        return () => {
            clearInterval(interval);
        }
    }, [mgr.Selection.ContentCellIsEdit, setMgr, is_loading]);


    React.useEffect(() => {
        setIsLoading(true);

        mgr_history.setOperationSender(async (op: Operation) => {

            const mgr = mgr_history.LastManager; // XXX:これは必ずしも正しいとは限らない

            debug_log("call storeJourneyMapOperationsEx");
            sequence_manager.Mutex.lock(async () => {
                const stored_op = await storeJourneyMapOperationsEx(mapId, op);
                const last_seq = sequence_manager.LastSeq;
                sequence_manager.put(mgr, stored_op);

                debug_log(`last_seq is ${last_seq} op.seq is ${stored_op.seq}`);

                await pushAnnouncement(new MapUpdate(mapId));
            });
        });

        getMapMeta(mapId)
            .then(map_meta => {
                setMapMeta(map_meta);
                loadJourneyMap(map_meta)
                    .then((mgr) => {
                        setMgr(mgr);
                        setStyleRegistry.start();
                        setIsLoading(false);
                    })
                    .catch((e) => {
                        // 修復未選択
                        debug_log(e);
                        navigate("/");
                    });
            })
            .catch((e) => {
                debug_log(e);
                window.alert("カスタマージャーニーマップを読み込めませんでした");
                navigate("/");
            });

        return () => {
            setStyleRegistry.stop();
        }
    }, [mapId]);

    const map_style = {
        width: mgr.MapWidth + (40 * 2)/* padding */, // この width を指定しないと、 Windowサイズを超えるmapが適切に表示されない
        display: "grid" as React.CSSProperties['display'],
        backgroundImage: mgr.BackgroundImageStyle,
        gridTemplateColumns: `${mgr.LineHeaderWidth}px 1fr`,
        gridTemplateRows: '80px ' + mgr.RoleHeaders.Height + ' ' + mgr.buildFunnelGridTemplateRows() + ' 1fr',
        gridGap: 10,
        zoom: zoom,
        paddingTop: 40 * (1 / zoom),
    }

    const cells_style = {
        display: "grid" as React.CSSProperties['display'],
        gridTemplateColumns: mgr.FunnelHeaders.GridTemplateColumns,
        gridTemplateRows: mgr.ContentHeaders.GridTemplateRows,
        gridGap: 10,
    };

    const content_header_style = {
        display: "grid" as React.CSSProperties['display'],
        gridTemplateColumns: mgr.LineHeaderWidth,
        gridTemplateRows: mgr.ContentHeaders.GridTemplateRows,
        gridGap: 10,
        left: mgr.Persona.IsShow ? persona_width : 0,
    };

    const handleChangeTitle = React.useCallback(async (value: string) => {

        mgr_history.push(mgr, new CJMapOp.OpUpdateTitle({title: value})).apply();

        await updateMapTitle(mapId, value);
    }, [mgr, mapId]);

    const handleClearSelect = React.useCallback(() => {
        if (mgr.Selection.ContentCellIsSelected) {
            mgr.clearSelect().apply();
        }
    }, [mgr]);

    const role_cell_handlers = React.useMemo(() => {
        return {
            handleSelectCell(role_key_pair: RoleKeyPair) {
                mgr.selectRoleCell(role_key_pair).apply();
            },
            handleCellNameChange(role_key: RoleKey, funnel_keys: FunnelKey[], name: string) {
                mgr_history.push(mgr, new CJMapOp.OpSetRoleCellName({role_key, funnel_keys, name})).apply();
            },
            handleJoinCell(role_cell: VisibleRoleCell) {
                const next_role_key_pair = role_cell.State.NextRoleKeyPairOrNull;
                if (next_role_key_pair === null) {
                    return;
                }

                const role_key = next_role_key_pair.LineKey;
                const funnel_keys = [next_role_key_pair.FunnelKey];
                if (role_cell.Name === VisibleCell.NOT_JOINABLE_VALUE) {
                    funnel_keys.push(role_cell.KeyPair.FunnelKey);
                }
                const name = role_cell.OstensibleName;

                mgr_history.push(mgr, new CJMapOp.OpSetRoleCellName({role_key, funnel_keys, name})).apply();
            },
            handleSplitCell(role_cell: VisibleRoleCell) {
                const joined_keys = role_cell.State.JoinedKeys;
                if (joined_keys.length < 2) {
                    return;
                }

                const role_key = role_cell.KeyPair.LineKey;
                const funnel_keys = [joined_keys[joined_keys.length - 1]];
                const name = VisibleCell.NOT_JOINABLE_VALUE;
                mgr_history.push(mgr, new CJMapOp.OpSetRoleCellName({role_key, funnel_keys, name})).apply();
            }
        }
    }, [mgr]);

    const role_header_handlers = React.useMemo(() => {
        return {
            handleNameChange: (role_key: RoleKey, name: string) => {
                mgr_history.push(mgr, new CJMapOp.OpSetRoleName({role_key, name})).apply();
            },
            handleRemove(role_key: RoleKey) {
                mgr_history.push(mgr, new CJMapOp.OpRemoveRole({role_key})).apply();
            },
            handleMove(role_key: RoleKey, to_role_key: RoleKey) {
                const position = mgr.RoleHeaders.findIndexByKey(to_role_key);
                mgr_history.push(mgr, new CJMapOp.OpMoveRole({role_key, position})).apply();
            },
        }
    }, [mgr]);

    const funnel_group_handlers = React.useMemo(() => {
        return {
            handleSelectCell: (funnel_key: FunnelKey) => {
                mgr.selectFunnelGroup(funnel_key).apply();
            },
            handleNameChange(funnel_keys: FunnelKey[], name: string) {
                mgr_history.push(mgr, new CJMapOp.OpSetFunnelGroupName({funnel_keys, name})).apply();
            },
            handleJoinCell: (cell: VisibleFunnelGroupCell) => {
                const next_funnel_key = cell.State.NextFunnelKeyOrNull;
                if (next_funnel_key === null) {
                    return;
                }

                const funnel_keys = [next_funnel_key];
                if (cell.Name === VisibleCell.NOT_JOINABLE_VALUE) {
                    funnel_keys.push(cell.Key);
                }
                const name = cell.OstensibleName;
                mgr_history.push(mgr, new CJMapOp.OpSetFunnelGroupName({funnel_keys, name})).apply();
            },
            handleSplitCell: (cell: VisibleFunnelGroupCell) => {
                const joined_keys = cell.State.JoinedKeys;
                if (joined_keys.length < 2) {
                    return;
                }

                const funnel_keys = [joined_keys[joined_keys.length - 1]];
                const name = VisibleCell.NOT_JOINABLE_VALUE;
                mgr_history.push(mgr, new CJMapOp.OpSetFunnelGroupName({funnel_keys, name})).apply();
            }
        }
    }, [mgr]);

    const funnel_handlers = React.useMemo(() => {
        return {
            handleSelectCell(funnel_key: FunnelKey) {
                mgr.selectMainFunnel(funnel_key).apply();
            },
            handleNameChange(funnel_key: FunnelKey, name: string) {
                mgr_history.push(mgr, new CJMapOp.OpSetFunnelName({funnel_key, name})).apply();
            },
            handleHide(funnel_key: FunnelKey) {
                mgr_history.push(
                    mgr, new CJMapOp.OpSetFunnelIsHidden({funnel_keys: [funnel_key], is_hidden: true})
                ).apply();
            },
            handleShow(funnel_key: FunnelKey) {
                mgr_history.push(
                    mgr, new CJMapOp.OpSetFunnelIsHidden({funnel_keys: [funnel_key], is_hidden: false})
                ).apply();
            },
            handleRemove(funnel_key: FunnelKey) {
                if (1 < mgr.FunnelHeaders.Length) {
                    mgr_history.push(mgr, new CJMapOp.OpRemoveFunnel({funnel_key})).apply()
                }
            },
            handleDragStart() {
                mgr.UnmergeTemporary().apply();
            },
            handleDragEnd(funnel_key: FunnelKey, to_funnel_key: FunnelKey) {
                if (funnel_key === to_funnel_key) {
                    mgr.remerge().apply();
                } else {
                    const position = mgr.FunnelHeaders.findIndexByKey(to_funnel_key);
                    mgr_history.push(mgr, new CJMapOp.OpMoveFunnel({funnel_key, position})).apply();
                }
            },
        }
    }, [mgr]);

    const content_header_handlers = React.useMemo(() => {
        return {
            handleNameChange(content_key: ContentKey, name: string) {
                mgr_history.push(mgr, new CJMapOp.OpSetContentName({content_key, name})).apply();
            },
            handleHide(content_key: ContentKey) {
                mgr_history.push(
                    mgr, new CJMapOp.OpSetContentIsHidden({content_keys: [content_key], is_hidden: true})
                ).apply();
            },
            handleShow(content_key: ContentKey) {
                mgr_history.push(
                    mgr, new CJMapOp.OpSetContentIsHidden({content_keys: [content_key], is_hidden: false})
                ).apply();
            },
            handleRemove(content_key: ContentKey) {
                if (1 < mgr.ContentHeaders.Length) {
                    mgr_history.push(mgr, new CJMapOp.OpRemoveContent({content_key})).apply();
                }
            },
            handleMove(content_key: ContentKey, to_content_key: ContentKey) {
                const position = mgr.ContentHeaders.findIndexByKey(to_content_key);
                mgr_history.push(mgr, new CJMapOp.OpMoveContent({content_key, position})).apply();
            }
        }
    }, [mgr]);

    const content_cell_handlers = React.useMemo(() => {
        return {
            handleSelectCell: (cell_key: ContentKeyPair) => {
                mgr.selectContentCell(cell_key).apply();
            },
            handleUnSelectCell: (cell_key: ContentKeyPair) => {
                mgr.clearSelect().apply();
            },
            handleCellEditStart: () => {
                mgr.editCell().apply();
            },
            handleChangeContent: (cell_key: ContentKeyPair, cell_content: CellContent) => {
                mgr.setData(cell_key, cell_content, null, null).apply();
            },
            handleCellEditEndWithChanged: (cell_key: ContentKeyPair, cell_content: CellContent, height: number | null, before_manager: CJMapManager) => {
                debug_log("ContentChanged (" + cell_key.toString() + ")", cell_content);

                const width = null; // 今のところ width 変更は無し
                mgr_history.push(before_manager, new CJMapOp.OpUpdateCellContent({
                    cell_key,
                    cell_content,
                    width,
                    height
                }))
                    .endEditCell().apply();
            },
            handleCellEditEndNoChanged: () => {
                mgr.endEditCell().apply();
            },
            handleCellResize: (cell_key: ContentKeyPair, rect: Rect) => {
                mgr_history.push(mgr, new CJMapOp.OpResizeCell({
                    cell_key,
                    width: rect.width,
                    height: rect.height
                })).apply();
            },
            handleCellSetHeight: (cell_key: ContentKeyPair, height: number) => {
                const new_mgr = mgr.growCellHeight(cell_key, height);
                if (new_mgr) {
                    new_mgr.apply();
                    return true;
                }

                return false;
            }
        }
    }, [mgr]);

    const header_toolbar_handlers = React.useMemo(() => {
        return {
            handleBackToDashBoard() {
                userName && pushAnnouncement(new UserLeave(mapId, userName));
                navigate("/");
            },
            handleUndo() {
                if (!mgr_history.HasUndoOperation || mgr.Selection.ContentCellIsEdit) {
                    return;
                }

                mgr_history.undo(mgr).apply();
            },
            handleRedo() {
                if (!mgr_history.HasRedoOperation || mgr.Selection.ContentCellIsEdit) {
                    return;
                }

                mgr_history.redo(mgr).apply();
            },

            handleChangeBackground: (background_key: string) => {
                mgr_history.push(mgr, new CJMapOp.OpUpdateBackground({background_key})).apply();
            },

            handleChangeColorScheme: (color_scheme_key: string) => {
                mgr_history.push(mgr, new CJMapOp.OpUpdateColorScheme({color_scheme_key})).apply();
            },

            handleChangeCellContent: (modifier: (content: CellContent) => CellContent) => {
                if (mgr.Selection.ContentCellIsSelected) {
                    const cell = mgr.BuiltContentCells.getSelectedCellOrNull();
                    if (cell === null) {
                        return;
                    }

                    const cell_content = modifier(cell.data);
                    if (cell.data.equals(cell_content)) {
                        return;
                    }

                    const cell_key = cell.KeyPair;
                    const width = cell.State.Width;
                    const height = cell.State.Height;

                    mgr_history.push(mgr, new CJMapOp.OpUpdateCellContent({
                        cell_key,
                        cell_content,
                        width,
                        height
                    })).apply();
                }
            },

            handleToggleBold: () => {
                if (mgr.Selection.ContentCellIsSelected) {
                    const cell = mgr.BuiltContentCells.getSelectedCellOrNull();
                    if (cell === null) {
                        return;
                    }

                    const cell_content = cell.data.toggleBold();
                    if (cell.data.equals(cell_content)) {
                        return;
                    }

                    const cell_key = cell.KeyPair;
                    const width = cell.State.Width;
                    const height = cell.State.Height;

                    mgr_history.push(mgr, new CJMapOp.OpUpdateCellContent({
                        cell_key,
                        cell_content,
                        width,
                        height
                    })).apply();
                }
            },

            handleAlign: (align: AlignmentType) => {
                if (mgr.Selection.ContentCellIsSelected) {
                    const cell = mgr.BuiltContentCells.getSelectedCellOrNull();
                    if (cell === null) {
                        return;
                    }

                    const cell_content = cell.data.setAlignment(align);
                    if (cell.data.equals(cell_content)) {
                        return;
                    }

                    const cell_key = cell.KeyPair;
                    const width = cell.State.Width;
                    const height = cell.State.Height;

                    mgr_history.push(mgr, new CJMapOp.OpUpdateCellContent({
                        cell_key,
                        cell_content,
                        width,
                        height
                    })).apply();
                }
            },

            handleJoin: () => {
                if (mgr.Selection.FunnelGroupCellIsSelected) {
                    const cell = mgr.BuiltFunnelGroupLine.getSelectedCellOrNull();
                    if (cell) {
                        funnel_group_handlers.handleJoinCell(cell);
                    }
                } else if (mgr.Selection.RoleCellIsSelected) {
                    const cell = mgr.BuiltRoleLines.getSelectedCellOrNull();
                    if (cell) {
                        role_cell_handlers.handleJoinCell(cell);
                    }
                }
            },
            handleSplit: () => {
                if (mgr.Selection.FunnelGroupCellIsSelected) {
                    const cell = mgr.BuiltFunnelGroupLine.getSelectedCellOrNull();
                    if (cell) {
                        funnel_group_handlers.handleSplitCell(cell);
                    }
                } else if (mgr.Selection.RoleCellIsSelected) {
                    const cell = mgr.BuiltRoleLines.getSelectedCellOrNull();
                    if (cell) {
                        role_cell_handlers.handleSplitCell(cell);
                    }
                }
            },
            makeHandleAddFunnel: (direction: "right" | "left") => {
                return () => {
                    const funnel = mgr.createFunnel();

                    let position = 0;

                    if (mgr.Selection.ContentCellIsSelected || mgr.Selection.MainFunnelCellIsSelected
                        || mgr.Selection.FunnelGroupCellIsSelected || mgr.Selection.RoleCellIsSelected) {
                        position = mgr.FunnelHeaders.findIndexByKey(mgr.Selection.SelectedFunnelKey);
                    }

                    if (direction === "right") {
                        if (mgr.Selection.FunnelGroupCellIsSelected) {
                            position += mgr.BuiltFunnelGroupLine.getSelectedCellOrNull()?.State.JoinedKeys.length ?? 1;
                        } else if (mgr.Selection.RoleCellIsSelected) {
                            position += mgr.BuiltRoleLines.getSelectedCellOrNull()?.State.JoinedKeys.length ?? 1;
                        } else {
                            position += 1;
                        }
                    }

                    debug_log("append position " + position);

                    mgr_history.push(mgr, new CJMapOp.OpAppendFunnel({funnel, position})).apply();

                    setMessage("列を追加しました");
                }
            },
            makeHandleAddRole: (direction: "above" | "below") => {
                return () => {
                    const role_header = mgr.createRoleHeader();
                    const position = mgr.Selection.RoleCellIsSelected
                        ? (mgr.RoleHeaders.findIndexByKey(mgr.Selection.SelectedRoleKey) + (direction === "above" ? 0 : 1))
                        : (direction === "above" ? mgr.RoleHeaders.FirstPosition : mgr.RoleHeaders.LastPosition);

                    mgr_history.push(mgr, new CJMapOp.OpAppendRole({role_header, position})).apply();

                    setMessage("行を追加しました");
                }
            },
            makeHandleAddContent: (editor_type: EditorType, direction: "above" | "below") => {
                return () => {

                    const content_header = mgr.createContentHeader(editor_type);
                    const position = mgr.Selection.ContentCellIsSelected
                        ? mgr.ContentHeaders.findIndexByKey(mgr.Selection.SelectedContentKey) + (direction === "above" ? 0 : 1)
                        : (direction === "above" ? mgr.ContentHeaders.FirstPosition : mgr.ContentHeaders.LastPosition);

                    mgr_history.push(mgr, new CJMapOp.OpAppendContent({content_header, position})).apply();

                    setMessage("行を追加しました");
                }
            },
            handleShowAllCell: () => {
                const ops: Operation[] = [];

                const funnel_keys = mgr.FunnelHeaders.HiddenKeys;
                if (0 < funnel_keys.length) {
                    ops.push(new CJMapOp.OpSetFunnelIsHidden({funnel_keys, is_hidden: false}));
                }

                const content_keys = mgr.ContentHeaders.HiddenKeys;
                if (0 < content_keys.length) {
                    ops.push(new CJMapOp.OpSetContentIsHidden({content_keys, is_hidden: false}));
                }

                if (0 < ops.length) {
                    mgr_history.multiPush(mgr, ops).apply();
                }
            },
        }
    }, [mgr, funnel_group_handlers, role_cell_handlers]);

    const persona_handlers = React.useMemo(() => {
        return {
            onChangeShowPersona(show: boolean) {
                mgr_history.push(mgr, new CJMapOp.OpSetShowPersona({show: show})).apply();
            },
            onChangeImageKey(image_key: string) {
                mgr_history.push(mgr, new CJMapOp.OpSetPersonaImage({persona_image_key: image_key})).apply();
            },
            onChangeTagLine(tagline: string) {
                mgr_history.push(mgr, new CJMapOp.OpSetPersonaTagline({tagline})).apply();
            },
            onChangeSpecsName(index: number, name: string) {
                mgr_history.push(mgr, new CJMapOp.OpSetPersonaSpecName({index, name})).apply();
            },
            onChangeSpecsValue(index: number, value: string) {
                mgr_history.push(mgr, new CJMapOp.OpSetPersonaSpecValue({index, value})).apply();
            },
            onChangeDescription(description: string) {
                mgr_history.push(mgr, new CJMapOp.OpSetPersonaDescription({description})).apply();
            },
            onResize(persona_width: number) {
                setPersonaWidth(persona_width);
            }
        }
    }, [mgr]);

    /******************************************************************************************
     * Global
     ******************************************************************************************/
    React.useEffect(() => {
        const keydown_handler = (e: KeyboardEvent) => {
            if (mgr.Selection.NotSelected) {
                return;
            }

            if (KeyboardEventUtil.isMoveLeftKey(e)) {
                mgr.moveToCell('left').apply();
                e.preventDefault();
            } else if (KeyboardEventUtil.isMoveRightKey(e)) {
                mgr.moveToCell('right').apply();
                e.preventDefault();
            } else if (KeyboardEventUtil.isMoveUpKey(e)) {
                mgr.moveToCell('up').apply();
                e.preventDefault();
            } else if (KeyboardEventUtil.isMoveDownKey(e)) {
                mgr.moveToCell('down').apply();
                e.preventDefault();
            }

            if (KeyboardEventUtil.isUndoKey(e)) {
                header_toolbar_handlers.handleUndo();
            }

            if (KeyboardEventUtil.isHelpKey(e)) {
                setShowShortcutPanel(true);
            }
        };
        document.addEventListener('keydown', keydown_handler);
        return () => {
            document.removeEventListener('keydown', keydown_handler);
        }
    }, [mgr, header_toolbar_handlers]);

    React.useEffect(() => {
        if (!userName) {
            return;
        }

        const visibilitychange = () => {
            if (document.visibilityState === 'hidden') {
                // ブラウザを閉じた際の処理
                pushAnnouncement(new UserLeave(mapId, userName));
            }
        };

        window.addEventListener('unload', visibilitychange);
        return () => {
            window.removeEventListener('unload', visibilitychange);
        }
    }, [mapId, userName]);

    /******************************************************************************************
     * Moving Cover
     ******************************************************************************************/
    const [is_moving, setIsMoving] = React.useState<boolean>(false);

    React.useEffect(() => {
        const keydown_handler = (e: KeyboardEvent) => {
            if (KeyboardEventUtil.isGripMonitorKey(e)) {
                setIsMoving(true);
                e.preventDefault();
            }
        };
        const keyup_handler = (e: KeyboardEvent) => {
            if (KeyboardEventUtil.isGripMonitorKey(e)) {
                setIsMoving(false);
                e.preventDefault();
            }
        }
        window.addEventListener('keydown', keydown_handler);
        window.addEventListener('keyup', keyup_handler);
        return () => {
            window.removeEventListener('keydown', keydown_handler);
            window.removeEventListener('keyup', keyup_handler);
        }
    }, []);


    /******************************************************************************************
     * Screen Capture
     ******************************************************************************************/
    const [now_printing, setNowPrinting] = React.useState(false);
    const handleScreenCapture = () => {
        if (now_printing) {
            return;
        }

        const elm = document.querySelector('.cjmap-persona-map');
        if (elm instanceof HTMLElement) {
            setNowPrinting(true);
            setMessage("現像しています");

            const title = elm.querySelector('.cjmap-title');
            let title_width = '';
            if (title instanceof HTMLElement) {
                title_width = title.style.width;
                title.style.width = '100%';
            }

            html2canvas(elm).then(function (canvas) {
                const link = document.createElement("a");
                link.download = 'cjmap.png';
                link.href = canvas.toDataURL('image/png');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                setNowPrinting(false);
            });

            if (title instanceof HTMLElement && title_width) {
                title.style.width = title_width;
            }
        }
    }

    /******************************************************************************************
     * preview 更新
     ******************************************************************************************/
    React.useEffect(() => {
        if (is_loading || !map_meta.isEditable()) {
            return;
        }

        let previous_preview = '';
        const updateScreenShot = async () => {
            if (!mapId) {
                throw new Error("no mapId");
            }

            const element = document.querySelector('.cjmap-map');
            if (!(element instanceof HTMLElement)) {
                throw new Error("unexpected");
            }

            const preview = await capturePreview(element);
            if (previous_preview !== preview) {
                previous_preview = preview;
                debug_log("update preview", new Date());
                updateMapPreview(mapId, preview);
                setMessage("プレビューを更新しました");
            }
        }

        const first_update = setTimeout(updateScreenShot, 10 * 1000);
        const interval_update = setInterval(updateScreenShot, 5 * 60 * 1000);
        return () => {
            clearTimeout(first_update);
            clearInterval(interval_update);
        }
    }, [mapId, is_loading, map_meta]);

    /******************************************************************************************
     * command
     ******************************************************************************************/
    React.useEffect(() => {

        let original_command: any = undefined;
        // @ts-ignore
        if (window.command) {
            // @ts-ignore
            original_command = window.command;
        }

        // @ts-ignore
        window.command = {
            dump_map: () => {
                console.log(mgr_history.LastManager.toJourneyMapJSONString());
            },
            crash: () => {
                mgr_history.push(mgr, new CJMapOp.OpUpdateCellContent({
                    cell_key: new ContentKeyPair("00000000" as ContentKey, "00000000" as FunnelKey),
                    cell_content: CellContent.createNew(),
                    width: 0,
                    height: 0
                }));
            },
            send_op_ex: async () => {
                const op = new CJMapOp.OpUpdateJourneyMap({journey_map: SerializableJourneyMap.fromJSONString(mgr_history.LastManager.toJourneyMapJSONString())});
                console.log(await storeJourneyMapOperationsEx(mapId, op));
            },
            apply_op: async (seq: number) => {
                const op = await fetchJourneyMapOperation(mapId, seq);
                sequence_manager.run(op).apply();
            },
            set_readonly: async () => {
                setMapMeta(new MapMeta(map_meta.MapId, map_meta.Version, 'viewer'));
            },
            dump_sequence_manager: () => {
                console.log(sequence_manager);
            },
            message: (message: string) => {
                setMessage(message);
            }
        };

        return () => {
            // @ts-ignore
            window.command = original_command;
        }

    }, [mgr]);

    /***************************************************************************************** */
    const [message, setMessage] = React.useState<string>('');

    return (
        <div className="cjmap-editor">
            <div className="cjmap-header">

                <Tooltip title={t("ダッシュボード")}>
                    <span className="page-back" onClick={header_toolbar_handlers.handleBackToDashBoard}/>
                </Tooltip>

                <Tooltip title={t("一つ戻す")}>
                    <span className="undo header-button header-button-square"
                          aria-disabled={map_meta.isReadOnly() || !mgr_history.HasUndoOperation || mgr.Selection.ContentCellIsEdit}
                          onClick={header_toolbar_handlers.handleUndo}
                    >&nbsp;</span>
                </Tooltip>
                <Tooltip title={t("一つ進む")}>
                    <span className="redo header-button header-button-square"
                          aria-disabled={map_meta.isReadOnly() || !mgr_history.HasRedoOperation || mgr.Selection.ContentCellIsEdit}
                          onClick={header_toolbar_handlers.handleRedo}
                    >&nbsp;</span>
                </Tooltip>

                <div>{/* spacer */}</div>
                <div>{/* spacer */}</div>
                <div>{/* spacer */}</div>

                <DropMenu containerClassName="letter-font"
                          label={mgr.Selection.ContentCellIsSelected ? mgr.BuiltContentCells.getSelectedCellOrNull()?.data.getTextStyle().FontLabel ?? '' : '---'}
                          tooltip={t("フォント")}
                          disabled={map_meta.isReadOnly() || !mgr.Selection.ContentCellIsNotEditSelected}
                          onChange={menu_name => header_toolbar_handlers.handleChangeCellContent(c => c.setFontName(menu_name))}>
                    <ul className="header-dropdown-menu">
                        <li data-drop_menu="sans_serif">{t("ゴシック")}</li>
                        <li data-drop_menu="serif">{t("明朝")}</li>
                    </ul>
                </DropMenu>

                <DropMenu containerClassName="letter-size"
                          label={mgr.Selection.ContentCellIsSelected ? mgr.BuiltContentCells.getSelectedCellOrNull()?.data.getTextStyle().SizeLabel ?? '' : '---'}
                          tooltip={t("文字サイズ")}
                          disabled={map_meta.isReadOnly() || !mgr.Selection.ContentCellIsNotEditSelected}
                          onChange={menu_name => header_toolbar_handlers.handleChangeCellContent(c => c.setFontSize(menu_name))}>
                    <ul className="header-dropdown-menu">
                        <li data-drop_menu='small'>{t("小")}</li>
                        <li data-drop_menu='normal'>{t("標準")}</li>
                        <li data-drop_menu='medium'>{t("大")}</li>
                        <li data-drop_menu='large'>{t("特大")}</li>
                    </ul>
                </DropMenu>

                <div>{/* spacer */}</div>

                <ColorSelectorButton className={""}
                                     tooltip={t("テキストの色")}
                                     color={mgr.Selection.ContentCellIsSelected ? mgr.BuiltContentCells.getSelectedCellOrNull()?.data.getTextStyle().ColorCode : undefined}
                                     disabled={map_meta.isReadOnly() || !mgr.Selection.ContentCellIsNotEditSelected}
                                     onChange={(color) => header_toolbar_handlers.handleChangeCellContent(c => c.setFontColor(color))}
                />

                <BoldButton className={""} tooltip={t("太字")}
                            active={mgr.Selection.ContentCellIsSelected ? mgr.BuiltContentCells.getSelectedCellOrNull()?.data.getTextStyle().IsBold ?? false : false}
                            disabled={map_meta.isReadOnly() || !mgr.Selection.ContentCellIsNotEditSelected}
                            onClick={header_toolbar_handlers.handleToggleBold}/>

                <div>{/* spacer */}</div>

                <AlignmentButtons
                    activeType={mgr.Selection.ContentCellIsSelected ? mgr.BuiltContentCells.getSelectedCellOrNull()?.data.getTextStyle().AlignmentType : undefined}
                    disabled={map_meta.isReadOnly() || !mgr.Selection.ContentCellIsNotEditSelected}
                    onClick={alignment_type => header_toolbar_handlers.handleAlign(alignment_type)}
                />

                <div>{/* spacer */}</div>

                <Tooltip title={t("選択したセルとひとつ右のセルを結合")}>
                    <span className="cell-join header-button header-button-string"
                          aria-disabled={map_meta.isReadOnly() || !mgr.IsJoinable}
                          onClick={header_toolbar_handlers.handleJoin}
                    >結合</span>
                </Tooltip>
                <Tooltip title={t("選択した結合セルの最も右のセルを結合解除")}>
                    <span className="cell-split header-button header-button-string"
                          aria-disabled={map_meta.isReadOnly() || !mgr.IsSeparatable}
                          onClick={header_toolbar_handlers.handleSplit}
                    >分割</span>
                </Tooltip>

                <div>{/* spacer */}</div>

                <DropMenu containerClassName="add-column-row"
                          label={t("列・行を追加")}
                          disabled={map_meta.isReadOnly()}
                          tooltip={""}>
                    <ul className="header-dropdown-menu add-column-row-menu">
                        <li className="menu-group" onClick={(e) => e.stopPropagation()}>{t("列を追加")}</li>
                        <li onClick={header_toolbar_handlers.makeHandleAddFunnel('left')}>{t("左に追加")}</li>
                        <li onClick={header_toolbar_handlers.makeHandleAddFunnel('right')}>{t("右に追加")}</li>
                        <li className="menu-group" onClick={(e) => e.stopPropagation()}>{t("行を追加")}</li>
                        <li onClick={header_toolbar_handlers.makeHandleAddRole('above')}>{t("フロー行を上に追加")}</li>
                        <li onClick={header_toolbar_handlers.makeHandleAddRole('below')}>{t("フロー行を下に追加")}</li>
                        <li onClick={header_toolbar_handlers.makeHandleAddContent('perception', 'above')}>{t("空白行を上に追加")}</li>
                        <li onClick={header_toolbar_handlers.makeHandleAddContent('perception', 'below')}>{t("空白行を下に追加")}</li>
                        <li onClick={header_toolbar_handlers.makeHandleAddContent('behavior', 'above')}>{t("行動行を上に追加")}</li>
                        <li onClick={header_toolbar_handlers.makeHandleAddContent('behavior', 'below')}>{t("行動行を下に追加")}</li>
                        <li onClick={header_toolbar_handlers.makeHandleAddContent('action', 'above')}>{t("施策行を上に追加")}</li>
                        <li onClick={header_toolbar_handlers.makeHandleAddContent('action', 'below')}>{t("施策行を下に追加")}</li>
                    </ul>
                </DropMenu>

                <div>{/* spacer */}</div>

                <Tooltip title={t("全て表示")}>
                    <span className="display-all header-button header-button-square"
                          aria-disabled={map_meta.isReadOnly() || !mgr.HasHiddenCell}
                          onClick={header_toolbar_handlers.handleShowAllCell}
                    >&nbsp;</span>
                </Tooltip>

                <div>{/* spacer */}</div>
                <div>{/* spacer */}</div>
                <div>{/* spacer */}</div>

                <DropMenu containerClassName="background-image"
                          label={t("背景")}
                          disabled={map_meta.isReadOnly()}
                          tooltip={t("背景を選択")}>
                    <div className="header-dropdown-menu" onClick={e => e.stopPropagation()}>
                        <BackgroundImageSelector onChange={header_toolbar_handlers.handleChangeBackground}/>
                    </div>
                </DropMenu>

                <DropMenu containerClassName="color-scheme"
                          label={t("配色")}
                          disabled={map_meta.isReadOnly()}
                          tooltip={t("配色を選択")}>
                    <ColorSchemeSelector className="header-dropdown-menu"
                                         onChange={header_toolbar_handlers.handleChangeColorScheme}/>
                </DropMenu>

                <div>{/* spacer */}</div>
                <div>{/* spacer */}</div>
                <div>{/* spacer */}</div>

                <Tooltip title={t("ショートカット")}>
                    <span className="shortcut-button header-button header-button-square"
                          onClick={() => setShowShortcutPanel(true)}
                    >?</span>
                </Tooltip>

                <div className={"split-spacer"}>{/* spacer */}</div>

                <Tooltip title={t("オンラインN人", {value: mgr.countOnlineUser().toString()})}>
                    <span className="online-button header-button header-button-string"
                          onClick={() => setShowOnlineUserDialog(true)}
                    >{mgr.countOnlineUser().toString()}</span>
                </Tooltip>

                <div>{/* spacer */}</div>

                <Tooltip title={t("共有設定")}>
                <span className="collaboration-button header-button header-button-string"
                      onClick={() => setShowCollaboratorDialog(true)}
                >{t("共有設定")}</span>
                </Tooltip>

                <div>{/* spacer */}</div>
                <div>{/* spacer */}</div>
                <div>{/* spacer */}</div>

                <Tooltip title={t("画像(png)でダウンロード")}>
                    <span className="screenshot header-button header-button-square"
                          onClick={handleScreenCapture}
                          aria-disabled={now_printing}
                    />
                </Tooltip>

                <div>{/* spacer */}</div>
                <div>{/* spacer */}</div>
                <div>{/* spacer */}</div>

                <DropMenu containerClassName="zoom"
                          label={(() => (zoom * 100) + '%')()}
                          tooltip={t("ズーム")}
                          onChange={(menu_name => {
                              const zoom = parseFloat(menu_name);
                              isNaN(zoom) ? setZoom(1.00) : setZoom(zoom);
                          })}
                >
                    <ul className="header-dropdown-menu">
                        <li data-drop_menu="0.25">25%</li>
                        <li data-drop_menu="0.50">50%</li>
                        <li data-drop_menu="0.75">75%</li>
                        <li data-drop_menu="1.00">100%</li>
                        <li data-drop_menu="1.25">125%</li>
                        <li data-drop_menu="1.50">150%</li>
                        <li data-drop_menu="1.75">175%</li>
                        <li data-drop_menu="2.00">200%</li>
                    </ul>
                </DropMenu>


            </div>

            <div className="cjmap-persona-map"
                 style={{width: map_style.width + (mgr.Persona.IsShow ? persona_width : 0) /* これを指定しないと、横長のマップで Persona が 左にはみ出る */}}>

                <Persona
                    showSize={mgr.Persona.IsShow ? persona_width : 0}
                    persona={mgr.Persona}
                    readOnly={map_meta.isReadOnly()}
                    onChangeImageKey={persona_handlers.onChangeImageKey}
                    onChangeTagLine={persona_handlers.onChangeTagLine}
                    onChangeSpecsName={persona_handlers.onChangeSpecsName}
                    onChangeSpecsValue={persona_handlers.onChangeSpecsValue}
                    onChangeDescription={persona_handlers.onChangeDescription}
                    onResize={persona_handlers.onResize}
                />

                <div className="cjmap-map" style={map_style} onClick={handleClearSelect}>

                    <PersonaButton className="cjmap-persona-button"
                                   tooltip={t("ペルソナ設定")}
                                   leftOffset={mgr.Persona.IsShow ? persona_width : 0}
                                   onClick={() => persona_handlers.onChangeShowPersona(!mgr.Persona.IsShow)}/>

                    <h1 className="cjmap-title"
                        style={{position: "sticky", left: 0, width: `min(100vw, ${mgr.MapWidth}px)`}}>
                        <ClickableInlineEditor
                            value={mgr.Title}
                            readOnly={map_meta.isReadOnly()}
                            onChange={handleChangeTitle}/>
                    </h1>

                    <RCRoleHeader role_headers={mgr.RoleHeaders}
                                  line_header_width={mgr.LineHeaderWidth}
                                  readOnly={map_meta.isReadOnly()}
                                  onNameChange={role_header_handlers.handleNameChange}
                                  onRemove={role_header_handlers.handleRemove}
                                  onMove={role_header_handlers.handleMove}
                    />

                    <RCRoleCells role_lines={mgr.BuiltRoleLines}
                                 role_headers={mgr.RoleHeaders}
                                 funnel_headers={mgr.FunnelHeaders}
                                 readOnly={map_meta.isReadOnly()}
                                 onSelectCell={role_cell_handlers.handleSelectCell}
                                 onCellNameChange={role_cell_handlers.handleCellNameChange}
                                 onJoinCell={role_cell_handlers.handleJoinCell}
                                 onSplitCell={role_cell_handlers.handleSplitCell}
                    />

                    <div className="funnel_header_cell" style={{gridRow: mgr.buildFunnelHeaderCellGridRow()}}>
                        {t("ファネル")}
                        <div className="grab-ui" data-enabled={!map_meta.isReadOnly()}
                             onClick={e => e.stopPropagation()}>
                            <Tooltip title={t("2行表示")}>
                            <span className="grab-ui-button funnel_ui_button funnel_ui_double"
                                  onClick={() => mgr.setFunnelType('double').apply()}/>
                            </Tooltip>
                            <Tooltip title={t("1行表示")}>
                            <span className="grab-ui-button funnel_ui_button funnel_ui_single"
                                  onClick={() => mgr.setFunnelType('single').apply()}/>
                            </Tooltip>
                        </div>
                    </div>

                    {mgr.IsVisibleFunnelGroupLine && (
                        <RCFunnelGroups
                            funnel_headers={mgr.FunnelHeaders} funnel_group={mgr.BuiltFunnelGroupLine}
                            readOnly={map_meta.isReadOnly()}
                            onSelectCell={funnel_group_handlers.handleSelectCell}
                            onNameChange={funnel_group_handlers.handleNameChange}
                            onJoinCell={funnel_group_handlers.handleJoinCell}
                            onSplitCell={funnel_group_handlers.handleSplitCell}
                        />
                    )}

                    <RCMainFunnelLine funnel_headers={mgr.FunnelHeaders}
                                      main_funnel_line={mgr.BuiltMainFunnelLine}
                                      readOnly={map_meta.isReadOnly()}
                                      onSelectCell={funnel_handlers.handleSelectCell}
                                      onNameChange={funnel_handlers.handleNameChange}
                                      onHide={funnel_handlers.handleHide}
                                      onShow={funnel_handlers.handleShow}
                                      onRemove={funnel_handlers.handleRemove}
                                      onDragStart={funnel_handlers.handleDragStart}
                                      onDragEnd={funnel_handlers.handleDragEnd}
                    />

                    <RCContentHeader style={content_header_style} mgr={mgr}
                                     readOnly={map_meta.isReadOnly()}
                                     onNameChange={content_header_handlers.handleNameChange}
                                     onHide={content_header_handlers.handleHide}
                                     onShow={content_header_handlers.handleShow}
                                     onRemove={content_header_handlers.handleRemove}
                        // onDragStart={() => undefined}
                                     onDragEnd={content_header_handlers.handleMove}
                    />

                    <div style={cells_style}>
                        {mgr.BuiltContentCells.map(cell =>
                            <RCContentCell key={cell.KeyPair.toString()} cell={cell} manager={mgr}
                                           readOnly={map_meta.isReadOnly()}
                                           onSelectCell={content_cell_handlers.handleSelectCell}
                                           onUnSelectCell={content_cell_handlers.handleUnSelectCell}
                                           onEditStart={content_cell_handlers.handleCellEditStart}
                                           onChangeContent={content_cell_handlers.handleChangeContent}
                                           onEditEndWithChanged={content_cell_handlers.handleCellEditEndWithChanged}
                                           onEditEndNoChanged={content_cell_handlers.handleCellEditEndNoChanged}
                                           onResize={content_cell_handlers.handleCellResize}
                                           onResizeTemporary={content_cell_handlers.handleCellSetHeight}
                            />
                        )}
                    </div>

                </div>

            </div>

            <MessageBar message={message}/>

            <OnlineUserDialog
                online_user_emails={mgr.listCollaboratorFocusUserEmails()}
                show={show_online_user_dialog}
                onClose={() => setShowOnlineUserDialog(false)}
            />

            {show_collaborator_dialog
            && map_meta.isOwner() ?
                <CollaboratorDialog
                    mapId={map_meta.MapId}
                    show={show_collaborator_dialog}
                    onClose={() => setShowCollaboratorDialog(false)}
                /> :
                <NonOwnerCollaboratorDialog
                    mapId={map_meta.MapId}
                    show={show_collaborator_dialog}
                    onClose={(revoke) => {
                        setShowConfirmRevoke(revoke);
                        setShowCollaboratorDialog(false);
                    }}/>
            }
            <ConfirmDialog title={t("アクセス権を削除")} ok={t("削除")} cancel={t("キャンセル")} okIsGray={true}
                           show={!map_meta.isOwner() && show_confirm_revoke}
                           onOk={async () => {
                               await API.graphql(graphqlOperation(
                                   revokeMapAccess,
                                   {mapId: mapId}
                               ));

                               setShowConfirmRevoke(false);
                               userName && pushAnnouncement(new UserLeave(mapId, userName));
                               navigate("/");
                           }}
                           onCancel={() => {
                               setShowConfirmRevoke(false);
                           }}
            >{t("「マップ名」へのアクセス権を本当に削除しますか？", {value: mgr.Title})}</ConfirmDialog>

            <ShortcutPanel
                show={show_shortcut_panel}
                onClose={()=> setShowShortcutPanel(false)}
            />

            {is_moving && <MovingCover/>}
            {is_loading && <div className={"loading-cover"}/>}
        </div>
    );
}
